import React, { useState, useEffect } from 'react';
import { 
  Upload, 
  Download, 
  Filter, 
  Trophy, 
  Users, 
  Calendar,
  BarChart3,
  FileSpreadsheet,
  RefreshCw,
  Eye,
  ChevronDown,
  ChevronUp,
  Trash2,
  X,
  AlertTriangle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import tournamentLeaderboardService from '../apis/tournamentLeaderboardService';
import { isAdminRole } from '../constants/roles';
import DateRangePicker from '../components/ui/date-range-picker';
import { CST_TIMEZONE, formatCstDate } from '../utils/cstDate';
import moment from 'moment-timezone';

const EMPTY_FILTERS = {
  tier: '',
  playerName: '',
  tournamentName: '',
  tournamentDate: '',
  startDate: '',
  endDate: '',
  sortBy: 'totalPoints',
  sortOrder: 'DESC',
  page: 1,
  limit: 50
};

const formatFilterDate = (ymd) => {
  const [year, month, day] = String(ymd || '').split('-');
  if (!year || !month || !day) return ymd;
  return `${month}/${day}/${year}`;
};

const activeDateRange = (filters) => {
  if (filters.startDate && filters.endDate) {
    const startDate = filters.startDate <= filters.endDate ? filters.startDate : filters.endDate;
    const endDate = filters.startDate <= filters.endDate ? filters.endDate : filters.startDate;
    return {
      startDate,
      endDate,
      label: `${formatFilterDate(startDate)} - ${formatFilterDate(endDate)}`
    };
  }
  return null;
};

const TournamentLeaderboardPage = () => {
  let canDownloadExcels = false;
  try {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    canDownloadExcels = isAdminRole(String(storedUser?.role || '').trim());
  } catch {
    canDownloadExcels = false;
  }

  // State management
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [uploadedTournaments, setUploadedTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters and pagination
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
    hasNextPage: false,
    hasPrevPage: false
  });

  // UI state
  const [showFilters, setShowFilters] = useState(true);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMultiUploadModal, setShowMultiUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const [playerTournaments, setPlayerTournaments] = useState([]);
  
  // Confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState(null);
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarTournaments, setCalendarTournaments] = useState({});
  const [hoveredTournament, setHoveredTournament] = useState(null);
  const [tournamentTopPlayers, setTournamentTopPlayers] = useState({});
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [uploadedFileHistory, setUploadedFileHistory] = useState([]);
  const [downloadingAllExcels, setDownloadingAllExcels] = useState(false);
  const [excelDatesOpen, setExcelDatesOpen] = useState(false);
  const [openExcelDate, setOpenExcelDate] = useState(null);

  // Fetch data on component mount
  useEffect(() => {
    fetchLeaderboard();
    fetchStats();
    fetchUploadedTournaments();
    fetchCalendarData();
    fetchUploadedFileHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      setError('');
      let response;
      const textFilters = {
        playerName: filters.playerName,
        tournamentName: filters.tournamentName,
        tier: filters.tier
      };
      if (filters.startDate && filters.endDate) {
        response = await tournamentLeaderboardService.getLeaderboardByDateRange(filters.startDate, filters.endDate, {
          limit: 2000,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          ...textFilters
        });
      } else {
        response = await tournamentLeaderboardService.getTournamentLeaderboard(filters);
      }
      
      if (response.success) {
        const rows = response.data?.leaderboard || [];
        setLeaderboard(rows);
        setPagination(response.data?.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: rows.length,
          itemsPerPage: rows.length || filters.limit,
          hasNextPage: false,
          hasPrevPage: false
        });
      } else {
        setError(response.error || 'Failed to fetch leaderboard');
      }
    } catch (err) {
      setError('Failed to fetch leaderboard data');
      console.error('Error fetching leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await tournamentLeaderboardService.getTournamentStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchUploadedTournaments = async () => {
    try {
      const response = await tournamentLeaderboardService.getUploadedTournaments();
      
      if (response.success) {
        setUploadedTournaments(response.data?.tournaments || []);
        
      }
    } catch (err) {
      console.error('Error fetching uploaded tournaments:', err);
    }
  };

  const fetchCalendarData = async () => {
    try {
      const response = await tournamentLeaderboardService.getTournamentCalendar();
      
      if (response.success) {
        processCalendarData(response.data?.calendar || []);
      }
    } catch (err) {
      console.error('Error fetching calendar data:', err);
    }
  };

  const formatUploadDateTime = (value) => {
    if (!value) return 'Date not recorded';
    return new Date(value).toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const fetchUploadedFileHistory = async () => {
    if (!canDownloadExcels) return;
    try {
      const response = await tournamentLeaderboardService.getUploadedFileHistory();
      if (response.success) {
        setUploadedFileHistory(response.data?.files || []);
      }
    } catch (err) {
      console.error('Error fetching uploaded file history:', err);
    }
  };

  const handleDownloadStoredFile = async (file) => {
    if (!canDownloadExcels) return;
    if (!file?.id || !file.canDownload) {
      toast.error('Original file is not available for this upload');
      return;
    }

    const response = await tournamentLeaderboardService.downloadUploadedFile(
      file.id,
      file.filename || file.name || 'leaderboard.xlsx'
    );
    if (!response.success) {
      toast.error(response.error || 'Failed to download file');
      return;
    }
    toast.success('File downloaded');
  };

  const filesBySheetDate = uploadedFileHistory.reduce((groups, file) => {
    const raw = file.sheetDate || file.uploadDate;
    const key = raw
      ? moment(raw).tz(CST_TIMEZONE).format('YYYY-MM-DD')
      : 'Unknown date';
    if (!groups[key]) groups[key] = [];
    groups[key].push(file);
    return groups;
  }, {});
  const sheetDateKeys = Object.keys(filesBySheetDate).sort((a, b) => b.localeCompare(a));
  const downloadableExcelCount = uploadedFileHistory.filter((file) => file.canDownload).length;

  const handleDownloadAllStoredFiles = async () => {
    if (!canDownloadExcels) return;
    if (downloadableExcelCount === 0) {
      toast.error('No stored Excel files are available to download');
      return;
    }
    setDownloadingAllExcels(true);
    try {
      const response = await tournamentLeaderboardService.downloadAllUploadedFiles();
      if (!response.success) {
        toast.error(response.error || 'Failed to download Excel files');
        return;
      }
      toast.success(`Downloaded ${downloadableExcelCount} Excel file(s)`);
    } finally {
      setDownloadingAllExcels(false);
    }
  };

  // Process calendar data from API
  const processCalendarData = (calendarEntries) => {
    const calendarData = {};
    
    calendarEntries.forEach(entry => {
      const date = new Date(entry.date);
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      
      if (!calendarData[dateKey]) {
        calendarData[dateKey] = [];
      }
      
      // Add each file as a separate tournament entry
      entry.files.forEach(file => {
        calendarData[dateKey].push({
          id: file.id,
          name: file.filename,
          tier: file.tier,
          playerCount: file.playerCount,
          tournamentType: file.tournamentType,
          eventKey: file.eventKey,
          fileSize: file.fileSize,
          uploadDate: file.uploadDate,
          uploadedBy: file.uploadedBy,
          canDownload: Boolean(file.canDownload),
          tournamentDate: date
        });
      });
    });
    
    setCalendarTournaments(calendarData);
  };


  const formatUploadChecks = (data) => {
    const checks = Array.isArray(data?.checks) ? data.checks : [];
    if (!checks.length) return data?.message || '';
    return checks.map((check) => {
      if (check.status === 'rejected') return `${check.fileName}: not scored. ${check.reason}`;
      const kind = check.kind === 'flight' ? 'Flight' : check.kind === 'final' ? 'Day Two' : 'Standalone';
      const warnings = Array.isArray(check.warnings) && check.warnings.length ? ` ${check.warnings.join(' ')}` : '';
      return `${check.fileName}: ${kind}, ${check.tier}, ${check.pointsMonth}, ${check.registrationPoints} registration, ${check.finishPoints} finish.${warnings}`;
    }).join(' ');
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    const validation = tournamentLeaderboardService.validateFileUpload(selectedFile);
    if (!validation.isValid) {
      toast.error(validation.errors.join(', '));
      return;
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');
      
      const response = await tournamentLeaderboardService.uploadExcelFile(selectedFile);
      
      console.log('response upload excel file', response);
      if (response.success) {
        const data = response.data || {};
        const localCount = data.playersCount ?? data.playerCount ?? 0;
        const forwarded = data.locationBreakdown?.forwarded || 0;
        const detail = formatUploadChecks(data);
        toast.success(detail || (forwarded
            ? `File processed: ${localCount} players on this lounge, ${forwarded} sent to the other lounge.`
            : `File processed successfully! ${localCount} players scored on this lounge.`));
        setShowUploadModal(false);
        setSelectedFile(null);
        fetchLeaderboard();
        fetchStats();
        fetchUploadedTournaments();
        fetchCalendarData();
        fetchUploadedFileHistory();
      } else {
        toast.error(response.error || 'Failed to process file');
      }
    } catch (err) {
      toast.error('Failed to upload file');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleMultipleFileUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    const validation = tournamentLeaderboardService.validateMultipleFileUpload(selectedFiles);
    if (!validation.isValid) {
      toast.error(validation.errors.join(', '));
      return;
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');
      
      const response = await tournamentLeaderboardService.uploadMultipleExcelFiles(selectedFiles);
      
      console.log('response upload multiple excel files', response);
      if (response.success) {
        const data = response.data || {};
        const localCount = data.playersCount ?? data.playerCount ?? 0;
        const forwarded = data.locationBreakdown?.forwarded || 0;
        const files = Array.isArray(data.files) ? data.files.join(', ') : '';
        const detail = formatUploadChecks(data);
        toast.success(detail || (forwarded
            ? `Files processed: ${localCount} players on this lounge, ${forwarded} sent to the other lounge.${files ? ` Files: ${files}` : ''}`
            : `Files processed successfully! ${localCount} players scored on this lounge.${files ? ` Files: ${files}` : ''}`));
        setShowMultiUploadModal(false);
        setSelectedFiles([]);
        fetchLeaderboard();
        fetchStats();
        fetchUploadedTournaments();
        fetchCalendarData();
        fetchUploadedFileHistory();
      } else {
        toast.error(response.error || 'Failed to process files');
      }
    } catch (err) {
      toast.error('Failed to upload files');
      console.error('Multi-upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateFinalLeaderboard = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const response = await tournamentLeaderboardService.generateFinalLeaderboard();
      
      if (response.success) {
        toast.success('Final combined leaderboard generated and downloaded successfully');
      } else {
        toast.error(response.error || 'Failed to generate final leaderboard');
        console.error('Final leaderboard generation error:', response);
      }
    } catch (err) {
      toast.error('Failed to generate final leaderboard');
      console.error('Final leaderboard generation error:', err);
    } finally {
      setLoading(false);
    }
  };
//TODO NEEDED IN FUTURE: SHUBHAM TRIBHUVAN 
  // const handleClearData = async () => {
  //   if (!window.confirm('Are you sure you want to clear all tournament data? This action cannot be undone.')) {
  //     return;
  //   }

  //   try {
  //     setLoading(true);
  //     setError('');
  //     setSuccess('');
      
  //     const response = await tournamentLeaderboardService.clearTournamentData();
      
  //     if (response.success) {
  //       toast.success(`Tournament data cleared successfully! ${response.data.deletedRecords} records deleted.`);
  //       setLeaderboard([]);
  //       setStats(null);
  //       setUploadedTournaments([]);
  //       setPagination({
  //         currentPage: 1,
  //         totalPages: 1,
  //         totalItems: 0,
  //         itemsPerPage: 50,
  //         hasNextPage: false,
  //         hasPrevPage: false
  //       });
  //     } else {
  //       toast.error(response.error || 'Failed to clear tournament data');
  //     }
  //   } catch (err) {
  //     toast.error('Failed to clear tournament data');
  //     console.error('Clear data error:', err);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // New function to show delete confirmation modal
  // const handleDeleteTournamentClick = (tournamentName) => {
  //   setTournamentToDelete(tournamentName);
  //   setShowDeleteModal(true);
  // };

  // Function to confirm tournament deletion
  const handleConfirmDeleteTournament = async () => {
    if (!tournamentToDelete) return;

    try {
      setLoading(true);
      setShowDeleteModal(false);
      
      const response = await tournamentLeaderboardService.deleteTournament(tournamentToDelete);
      
      if (response.success) {
        const message = tournamentToDelete === 'Combined Leaderboard' 
          ? `All tournament data cleared successfully! ${response.data.deletedRecords} records removed.`
          : `Tournament "${tournamentToDelete}" deleted successfully! ${response.data.deletedRecords} records removed.`;
        toast.success(message);
        
        // Refresh all data to reflect changes
        await Promise.all([
          fetchLeaderboard(),
          fetchStats(),
          fetchUploadedTournaments(),
          fetchCalendarData(),
          fetchUploadedFileHistory()
        ]);
      } else {
        toast.error(response.error || 'Failed to delete tournament');
      }
    } catch (err) {
      toast.error('Failed to delete tournament');
      console.error('Delete tournament error:', err);
    } finally {
      setLoading(false);
      setTournamentToDelete(null);
    }
  };

  // Function to cancel tournament deletion
  const handleCancelDeleteTournament = () => {
    setShowDeleteModal(false);
    setTournamentToDelete(null);
  };

  // Function to handle file delete click from calendar
  const handleDeleteFileClick = (tournament) => {
    setFileToDelete(tournament);
    setShowDeleteFileModal(true);
  };

  // Function to confirm file deletion
  const handleConfirmDeleteFile = async () => {
    if (!fileToDelete) return;

    try {
      setLoading(true);
      setShowDeleteFileModal(false);
      
      const response = await tournamentLeaderboardService.deleteFile(
        fileToDelete.eventKey,
        fileToDelete.name
      );
      
      if (response.success) {
        toast.success(`File "${fileToDelete.name}" deleted successfully! ${response.data.deletedEntries} entries removed.`);
        
        // Refresh all data to reflect changes
        await Promise.all([
          fetchLeaderboard(),
          fetchStats(),
          fetchUploadedTournaments(),
          fetchCalendarData(),
          fetchUploadedFileHistory()
        ]);
      } else {
        toast.error(response.error || 'Failed to delete file');
      }
    } catch (err) {
      toast.error('Failed to delete file');
      console.error('Delete file error:', err);
    } finally {
      setLoading(false);
      setFileToDelete(null);
    }
  };

  // Function to cancel file deletion
  const handleCancelDeleteFile = () => {
    setShowDeleteFileModal(false);
    setFileToDelete(null);
  };

  const fetchPlayerTournaments = async (playerName, playerId) => {
    try {
      const range = activeDateRange(filters);
      const response = await tournamentLeaderboardService.getPlayerTournamentDetails(
        playerName,
        playerId,
        range ? { startDate: range.startDate, endDate: range.endDate } : {}
      );
      if (response.success) {
        setPlayerTournaments(response.data?.tournaments || []);
      }
    } catch (err) {
      console.error('Error fetching player tournaments:', err);
    }
  };

  // Function to fetch cumulative top players up to a specific date
  const fetchCumulativeTopPlayers = async (endDate) => {
    try {
      // Call the new cumulative API endpoint
      const response = await tournamentLeaderboardService.getCumulativeLeaderboardByDate(
        endDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
        {
          limit: 9,
          sortBy: 'totalPoints',
          sortOrder: 'DESC'
        }
      );
      
      if (response.success) {
        return response.data?.leaderboard || [];
      }
      
      return [];
    } catch (err) {
      console.error('Error fetching cumulative top players:', err);
      return [];
    }
  };

  // Function to handle tournament hover
  const handleTournamentHover = async (tournament, event) => {
    setHoveredTournament(tournament);
    setMousePosition({ x: event.clientX, y: event.clientY });
    
    // Create a unique key for this date-based cumulative data
    const dateKey = tournament.tournamentDate.toISOString().split('T')[0];
    
    // Fetch cumulative top players if not already cached
    if (!tournamentTopPlayers[dateKey]) {
      const cumulativePlayers = await fetchCumulativeTopPlayers(tournament.tournamentDate);
      setTournamentTopPlayers(prev => ({
        ...prev,
        [dateKey]: cumulativePlayers
      }));
    }
  };

  // Function to handle tournament hover end
  const handleTournamentHoverEnd = () => {
    setHoveredTournament(null);
  };

  const handlePlayerExpand = (player) => {
    if (expandedPlayer === `${player.playerName}-${player.playerId}`) {
      setExpandedPlayer(null);
      setPlayerTournaments([]);
    } else {
      setExpandedPlayer(`${player.playerName}-${player.playerId}`);
      fetchPlayerTournaments(player.playerName, player.playerId);
    }
  };

  const handleFilterChange = (key, value) => {
    setExpandedPlayer(null);
    setPlayerTournaments([]);
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const clearFilters = () => {
    setExpandedPlayer(null);
    setPlayerTournaments([]);
    setFilters({ ...EMPTY_FILTERS });
  };

  const handleDateRangeApply = (startDate, endDate) => {
    const start = formatCstDate(startDate, 'YYYY-MM-DD');
    const end = formatCstDate(endDate, 'YYYY-MM-DD');
    setExpandedPlayer(null);
    setPlayerTournaments([]);
    setFilters(prev => ({
      ...prev,
      startDate: start <= end ? start : end,
      endDate: start <= end ? end : start,
      page: 1
    }));
    setIsDatePickerOpen(false);
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleExportCSV = () => {
    tournamentLeaderboardService.exportToCSV(leaderboard, 'tournament-leaderboard');
  };

  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return "st";
    if (j === 2 && k !== 12) return "nd";
    if (j === 3 && k !== 13) return "rd";
    return "th";
  };

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      const response = await tournamentLeaderboardService.generateHTMLReport();
      
      if (response.success) {
        toast.success('HTML report generated and downloaded successfully');
      } else {
        toast.error(response.error || 'Failed to generate HTML report');
        console.error('Report generation error:', response);
      }
    } catch (err) {
      toast.error('Failed to generate HTML report');
      console.error('Report generation error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calendar navigation functions
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateYear = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(currentDate.getFullYear() + direction);
    setCurrentDate(newDate);
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      const dateKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
      const isCurrentMonth = current.getMonth() === month;
      const isToday = current.toDateString() === new Date().toDateString();
      const tournaments = calendarTournaments[dateKey] || [];
      
      days.push({
        date: new Date(current),
        dateKey,
        isCurrentMonth,
        isToday,
        tournaments
      });
      
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  const getTierColor = (tier) => {
    const colors = {
      freeroll: 'bg-green-100 text-green-800',
      under10k: 'bg-blue-100 text-blue-800',
      '10k-25k': 'bg-yellow-100 text-yellow-800',
      '25k+': 'bg-red-100 text-red-800'
    };
    return colors[tier] || 'bg-gray-100 text-gray-800';
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const filterOptions = tournamentLeaderboardService.getFilterOptions();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8 justify-center items-center">
      <h1 className="text-3xl font-bold text-gray-800 text-center">
          <Trophy className="text-yellow-500 mx-auto" size={32} />
          Tournament Leaderboard
        </h1>
        <p className="text-gray-600 text-center">Manage and view tournament leaderboard data from Excel uploads</p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}
      
      {error && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 justify-center items-center md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md border">
            <div className="flex items-center">
              <Users className="text-blue-500" size={24} />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Players</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overall?.totalPlayers || 0}</p>
              </div>
            </div>
          </div>
          
        
          
          <div className="bg-white p-6 rounded-lg shadow-md border">
            <div className="flex items-center">
              <Trophy className="text-yellow-500" size={24} />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Points</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overall?.totalPoints || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md border">
            <div className="flex items-center">
              <BarChart3 className="text-purple-500" size={24} />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Average Points</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overall?.averagePoints || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Uploaded Tournaments Section */}
      {uploadedTournaments.length > 0 && (
        <div className="mb-8 justify-center items-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileSpreadsheet className="text-blue-500" size={24} />
            Uploaded Tournaments
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {uploadedTournaments.map((tournament, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow-md border hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-gray-900 text-sm">{tournament.tournamentName}</h3>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTierColor(tournament.tier)}`}>
                      {tournament.tier}
                    </span>
                      {/* <button
                        onClick={() => handleDeleteTournamentClick(tournament.tournamentName)}
                        className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                        title={tournament.tournamentName === 'Combined Leaderboard' ? 'Clear all tournament data' : 'Delete tournament'}
                        disabled
                     >
                        <X size={16} />
                      </button> */}
                  </div>
                </div>
                <div className="space-y-1 text-xs text-gray-600">
                  <p><strong>Players:</strong> {tournament.playerCount}</p>
                  <p><strong>Total Points:</strong> {tournament.totalPoints}</p>
                  {tournament.buyIn && <p><strong>Buy-In:</strong> {tournament.buyIn}</p>}
                  {tournament.prizePool && <p><strong>Prize Pool:</strong> {tournament.prizePool}</p>}
                  {tournament.guarantee && <p><strong>Guarantee:</strong> {tournament.guarantee}</p>}
                  <p><strong>Uploaded:</strong> {new Date(tournament.uploadedAt).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tournament Calendar Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="text-purple-500" size={24} />
          Tournament Calendar
        </h2>
        
        <div className="bg-white rounded-lg shadow-md border">
          {/* Calendar Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigateYear(-1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Previous Year"
                >
                  <ChevronLeft size={20} />
                </button>
                <h3 className="text-lg font-semibold text-gray-900">
                  {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
                <button
                  onClick={() => navigateYear(1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Next Year"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Previous Month"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => navigateMonth(1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Next Month"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
            
            {/* Tournament Summary */}
            <div className="mt-3 flex items-center justify-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Uploaded: {Object.values(calendarTournaments).flat().length} tournaments</span>
              </div>
              <div className="text-gray-400">
                • Shows tournament dates from filenames
              </div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="p-4">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {generateCalendarDays().map((day, index) => (
                <div
                  key={index}
                  className={`
                    min-h-[80px] p-2 border border-gray-200 rounded-lg relative
                    ${!day.isCurrentMonth ? 'bg-gray-50 text-gray-400' : 'bg-white'}
                    ${day.isToday ? 'ring-2 ring-blue-500 bg-blue-50' : ''}
                    hover:bg-gray-50 transition-colors cursor-pointer
                  `}
                >
                  <div className="text-sm font-medium mb-1">
                    {day.date.getDate()}
                  </div>
                  
                  {/* Tournament Indicators */}
                  {day.tournaments.length > 0 && (
                    <div className="space-y-1">
                      {day.tournaments.slice(0, 2).map((tournament, idx) => (
                        <div
                          key={idx}
                          className={`
                            text-xs px-1 py-0.5 rounded text-white truncate cursor-pointer relative group
                            ${tournament.tier === 'freeroll' ? 'bg-green-500 hover:bg-green-600' : 
                              tournament.tier === 'under10k' ? 'bg-blue-500 hover:bg-blue-600' :
                              tournament.tier === '10k-25k' ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-red-500 hover:bg-red-600'}
                            transition-colors
                          `}
                          onMouseEnter={(e) => {
                            // Only show tooltip if not hovering over delete button
                            if (!e.target.closest('button')) {
                              handleTournamentHover(tournament, e);
                            }
                          }}
                          onMouseLeave={handleTournamentHoverEnd}
                          onClick={(e) => {
                            if (e.target.closest('button')) return;
                            if (!canDownloadExcels) return;
                            handleDownloadStoredFile(tournament);
                          }}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span className="flex-1 truncate pr-1">
                              {tournament.name.length > 15 ? 
                                tournament.name.substring(0, 15) + '...' : 
                                tournament.name
                              }
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleTournamentHoverEnd(); // Hide tooltip when clicking delete
                                handleDeleteFileClick(tournament);
                              }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                handleTournamentHoverEnd(); // Hide tooltip when hovering delete button
                              }}
                              className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 hover:bg-black/30 rounded p-0.5 flex items-center justify-center"
                              title="Delete this file"
                            >
                              <X size={12} className="text-white" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {day.tournaments.length > 2 && (
                        <div className="text-xs text-gray-500">
                          +{day.tournaments.length - 2} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Calendar Legend */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="text-center text-sm text-gray-600 mb-3">
              <strong>Tournament Calendar</strong> - Shows when tournaments actually happened (based on filenames)
            </div>
            <div className="flex items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span>Freeroll</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span>Under 10K</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                <span>10K-25K</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded"></div>
                <span>25K+</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tournament Tooltip */}
      {hoveredTournament && (
        <div 
          className="fixed z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-4 max-w-sm pointer-events-none"
          style={{
            left: `${mousePosition.x + 10}px`,
            top: `${mousePosition.y - 10}px`,
            transform: 'translateY(-100%)'
          }}
        >
          <div className="mb-3">
            <h4 className="font-semibold text-gray-900 text-sm">{hoveredTournament.name}</h4>
            <p className="text-xs text-gray-600">
              {hoveredTournament.playerCount} players • {hoveredTournament.tournamentDate.toLocaleDateString()}
            </p>
            <p className="text-xs text-gray-700 mt-2">
              <span className="font-medium">Uploaded by:</span> {hoveredTournament.uploadedBy || 'Uploader not recorded'}
            </p>
            <p className="text-xs text-gray-700">
              <span className="font-medium">Date:</span> {formatUploadDateTime(hoveredTournament.uploadDate)}
            </p>
            {canDownloadExcels && hoveredTournament.canDownload && (
              <p className="text-[11px] text-blue-700 mt-1">Click the file to download the stored copy.</p>
            )}
            <div className={`inline-block px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
              hoveredTournament.tier === 'freeroll' ? 'bg-green-100 text-green-800' : 
              hoveredTournament.tier === 'under10k' ? 'bg-blue-100 text-blue-800' :
              hoveredTournament.tier === '10k-25k' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
            }`}>
              {hoveredTournament.tier}
            </div>
          </div>
          
          <div>
            <h5 className="font-medium text-gray-800 text-sm mb-2">Cumulative Top 9 Players (Up to {hoveredTournament.tournamentDate.toLocaleDateString()}):</h5>
            {(() => {
              const dateKey = hoveredTournament.tournamentDate.toISOString().split('T')[0];
              const players = tournamentTopPlayers[dateKey];
              
              if (players) {
                return (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {players.map((player, index) => (
                      <div key={index} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-600">#{index + 1}</span>
                          <span className="font-medium text-gray-900">{player.playerName}</span>
                        </div>
                        <div className="text-gray-600">
                          {player.totalPoints} pts
                        </div>
                      </div>
                    ))}
                  </div>
                );
              } else {
                return (
                  <div className="text-xs text-gray-500 flex items-center gap-2">
                    <RefreshCw className="animate-spin" size={12} />
                    Loading cumulative data...
                  </div>
                );
              }
            })()}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 mb-6">
        <button
          onClick={() => setShowUploadModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Upload size={20} />
          Upload Single File
        </button>
        
        <button
          onClick={() => setShowMultiUploadModal(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
        >
          <Upload size={20} />
          Upload Multiple Files
        </button>
        
        <button
          onClick={handleGenerateFinalLeaderboard}
          disabled={leaderboard.length === 0}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 disabled:bg-gray-400 flex items-center gap-2"
        >
          <Trophy size={20} />
          Generate Final Leaderboard
        </button>
        
        <button
          onClick={handleExportCSV}
          disabled={leaderboard.length === 0}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
        >
          <Download size={20} />
          Export CSV
        </button>
        
        <button
          onClick={handleGenerateReport}
          disabled={leaderboard.length === 0}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 flex items-center gap-2"
        >
          <FileSpreadsheet size={20} />
          Generate HTML Report
        </button>
        
        <button
          onClick={() => {
            fetchLeaderboard();
            fetchStats();
            fetchUploadedTournaments();
          }}
          className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
        >
          <RefreshCw size={20} />
          Refresh
        </button>
        
        {/* <button
          onClick={handleClearData}
          disabled
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2"
        >
          <Trash2 size={20} />
          Clear Data
        </button> */}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md border mb-6">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50"
        >
          <div className="flex items-center gap-2">
            <Filter size={20} />
            <span className="font-medium">Filters & Search</span>
            <span className="text-sm font-medium text-blue-700">{activeDateRange(filters)?.label || 'All time'}</span>
          </div>
          {showFilters ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        
        {showFilters && (
          <div className="p-4 border-t border-gray-200 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tier</label>
                <select
                  value={filters.tier}
                  onChange={(e) => handleFilterChange('tier', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">All Tiers</option>
                  {filterOptions.tiers.map(tier => (
                    <option key={tier.value} value={tier.value}>{tier.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Player</label>
                <input
                  type="text"
                  value={filters.playerName}
                  onChange={(e) => handleFilterChange('playerName', e.target.value)}
                  placeholder="Name or player ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tournament</label>
                <input
                  type="text"
                  value={filters.tournamentName}
                  onChange={(e) => handleFilterChange('tournamentName', e.target.value)}
                  placeholder="Tournament name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort</label>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={filters.sortBy}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    {filterOptions.sortOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <select
                    value={filters.sortOrder}
                    onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    {filterOptions.sortOrders.map(order => (
                      <option key={order.value} value={order.value}>{order.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <p className="text-sm font-semibold text-gray-900">Date Range</p>
                <p className="text-sm text-blue-800">
                  Showing {activeDateRange(filters)?.label || 'all time'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsDatePickerOpen(true)}
                className="w-full sm:w-auto inline-flex items-center justify-start px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm font-normal hover:bg-gray-50"
              >
                <Calendar className="mr-2 h-4 w-4 shrink-0" />
                <span className="truncate">{activeDateRange(filters)?.label || 'All time'}</span>
              </button>
              <p className="text-xs text-gray-600 mt-3">
                Choose a range the same way as Promos and Petty Cash. Clear all puts every field back and shows the full leaderboard. View Details uses the same dates.
              </p>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-2 rounded-lg bg-gray-800 text-white text-sm font-medium hover:bg-gray-900"
              >
                Clear all
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Leaderboard Table */}
      <div className="bg-white rounded-lg shadow-md border">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Tournament Leaderboard
              </h3>
              <p className="text-sm text-gray-600">
                Showing {leaderboard.length} of {pagination.totalItems || leaderboard.length} players
                <span className="ml-2 text-blue-700 font-medium">
                  · {activeDateRange(filters)?.label || 'All time'}
                </span>
              </p>
            </div>
            {filters.tournamentDate && (
              <button
                onClick={() => handleFilterChange('tournamentDate', '')}
                className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1"
                title="Clear date filter"
              >
                <X size={16} />
                Clear Date Filter
              </button>
            )}
          </div>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="animate-spin mx-auto mb-4" size={32} />
            <p className="text-gray-600">Loading leaderboard...</p>
          </div>
        ) : leaderboard.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Points</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tournament Points</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tournaments</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leaderboard.map((player, index) => (
                  <React.Fragment key={`${player.playerName}-${player.playerId}`}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {getRankBadge(index + 1)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {player.playerName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {player.playerId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {player.totalPoints}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {player.registrationPoints}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {player.tournamentPoints}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {player.tournamentsPlayed}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTierColor(player.tier)}`}>
                          {player.tier}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={() => handlePlayerExpand(player)}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Eye size={16} />
                          {expandedPlayer === `${player.playerName}-${player.playerId}` ? 'Hide' : 'View'} Details
                        </button>
                      </td>
                    </tr>
                    
                    {/* Expanded Player Details */}
                    {expandedPlayer === `${player.playerName}-${player.playerId}` && (
                      <tr>
                        <td colSpan="9" className="px-6 py-4 bg-gray-50">
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div className="bg-white rounded-lg border p-4">
                                <p className="text-xs text-gray-500">Registration Points</p>
                                <p className="text-lg font-semibold text-gray-900">{player.registrationPoints}</p>
                              </div>
                              <div className="bg-white rounded-lg border p-4">
                                <p className="text-xs text-gray-500">Tournament Points</p>
                                <p className="text-lg font-semibold text-gray-900">{player.tournamentPoints}</p>
                              </div>
                              <div className="bg-white rounded-lg border p-4">
                                <p className="text-xs text-gray-500">Total Points</p>
                                <p className="text-lg font-semibold text-gray-900">{player.totalPoints}</p>
                              </div>
                              <div className="bg-white rounded-lg border p-4">
                                <p className="text-xs text-gray-500">Tournaments Played</p>
                                <p className="text-lg font-semibold text-gray-900">{player.tournamentsPlayed}</p>
                              </div>
                            </div>

                            <div>
                              <h4 className="font-semibold text-gray-900">Tournament Details</h4>
                              <p className="text-sm text-gray-600">
                                {activeDateRange(filters) ? `For ${activeDateRange(filters).label}` : 'All recorded tournaments'}
                              </p>
                            </div>
                            {playerTournaments.length > 0 ? (
                              <div className="space-y-3">
                                {playerTournaments.map((tournament, idx) => (
                                  <div key={`${tournament.tournament}-${tournament.date || idx}`} className="border-l-4 border-blue-400 pl-4 py-2 bg-white rounded">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                      <div>
                                        <p className="text-sm font-medium text-gray-600">Tournament</p>
                                        <p className="text-sm font-bold text-gray-900">{tournament.tournament}</p>
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-600">Date</p>
                                        <p className="text-sm text-gray-900">
                                          {tournament.date
                                            ? new Date(tournament.date).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })
                                            : '—'}
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-600">{tournament.type === 'registration' ? 'Entry' : 'Place'}</p>
                                        <p className="text-sm text-gray-900">
                                          {tournament.type === 'registration' ? 'Registration' : (
                                            <>
                                              {tournament.place}
                                              {typeof tournament.place === 'number' ? getOrdinalSuffix(tournament.place) : ''}
                                            </>
                                          )}
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-gray-600">Points Earned</p>
                                        <p className="text-sm font-bold text-blue-600">+{tournament.points} pts</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="bg-white rounded-lg border p-4 text-sm text-gray-700">
                                <p>No tournaments in this date range.</p>
                                <p className="mt-1">Registration points: <span className="font-semibold">{player.registrationPoints}</span></p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <Trophy size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No leaderboard data found</p>
            <p className="text-sm">Upload an Excel file to get started</p>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-700">
              Showing page {pagination.currentPage} of {pagination.totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {canDownloadExcels && (
      <div className="mt-8 mb-10 bg-white rounded-lg shadow-md border">
        <div className="p-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileSpreadsheet className="text-green-600" size={20} />
              Uploaded Excel files
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {downloadableExcelCount > 0
                ? `${downloadableExcelCount} stored file(s), grouped by the sheet date.`
                : 'No stored Excel file is available yet.'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDownloadAllStoredFiles}
            disabled={downloadingAllExcels || downloadableExcelCount === 0}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
          >
            <Download size={18} />
            {downloadingAllExcels ? 'Preparing download...' : 'Download all Excel files'}
          </button>
        </div>
        {sheetDateKeys.length > 0 && (
          <div className="border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                setExcelDatesOpen((open) => !open);
                setOpenExcelDate(null);
              }}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
            >
              <span className="text-sm font-medium text-gray-800">
                {excelDatesOpen ? 'Hide dates' : `Show files by date (${sheetDateKeys.length})`}
              </span>
              {excelDatesOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {excelDatesOpen && (
              <div className="border-t border-gray-200">
                {sheetDateKeys.map((day) => {
                  const open = openExcelDate === day;
                  const files = filesBySheetDate[day];
                  return (
                    <div key={day} className="border-b border-gray-100 last:border-b-0">
                      <button
                        type="button"
                        onClick={() => setOpenExcelDate(open ? null : day)}
                        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
                      >
                        <span className="text-sm font-semibold text-gray-800">{day}</span>
                        <span className="flex items-center gap-2 text-xs text-gray-500">
                          {files.length} file{files.length === 1 ? '' : 's'}
                          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </span>
                      </button>
                      {open && (
                        <div className="px-4 pb-3 space-y-2">
                          {files.map((file) => (
                            <div key={file.id} className="flex items-center justify-between gap-3 text-sm">
                              <div className="min-w-0">
                                <div className="text-gray-900 truncate">{file.filename}</div>
                                <div className="text-xs text-gray-500">
                                  {file.uploadedBy || 'Uploader not recorded'} · {formatUploadDateTime(file.uploadDate)}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleDownloadStoredFile(file)}
                                disabled={!file.canDownload}
                                className="shrink-0 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                              >
                                <Download size={16} />
                                {file.canDownload ? 'Download' : 'Unavailable'}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg w-11/12 max-w-md">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Upload Single Excel File</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Excel File (.xlsx, .xls)
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum file size: 10MB. Type mcallen or brownsville in Location to split lounges. Cage1 or blank stays on this site.
                </p>
              </div>
              
              {selectedFile && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Selected:</strong> {selectedFile.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleFileUpload}
                disabled={!selectedFile || uploading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Upload
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Multi-File Upload Modal */}
      {showMultiUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg w-11/12 max-w-2xl">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Upload Multiple Excel Files</h3>
              <button
                onClick={() => setShowMultiUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Multiple Excel Files (.xlsx, .xls)
                </label>
                <input
                  type="file"
                  name="excelFiles"
                  accept=".xlsx,.xls"
                  multiple
                  onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum 20 files, 10MB each. Supports multi-flight tournaments (1A, 1B, Day 2, Final, etc.). Location mcallen/brownsville splits lounges; Cage1 or blank stays here.
                </p>
              </div>
              
              {selectedFiles.length > 0 && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700 mb-2">
                    <strong>Selected Files ({selectedFiles.length}):</strong>
                  </p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="text-xs text-gray-600 flex justify-between">
                        <span>{file.name}</span>
                        <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Multi-Flight Tournament Support:</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Automatically groups flights (1A, 1B, 1C, 1D) and finals (Day 2, Final)</li>
                  <li>• Awards registration points per flight, tournament points from final placement</li>
                  <li>• Handles both standalone tournaments and multi-flight events</li>
                  <li>• Supports tournament tier detection based on guarantee amounts</li>
                </ul>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowMultiUploadModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleMultipleFileUpload}
                disabled={selectedFiles.length === 0 || uploading}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Upload {selectedFiles.length} Files
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0">
                  <AlertTriangle className="text-red-500" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {tournamentToDelete === 'Combined Leaderboard' ? 'Clear All Tournament Data' : 'Delete Tournament'}
                </h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                {tournamentToDelete === 'Combined Leaderboard' 
                  ? 'Are you sure you want to clear all tournament data? This will remove all leaderboard data and cannot be undone.'
                  : `Are you sure you want to delete the tournament "${tournamentToDelete}"? This will remove all data related to this tournament and cannot be undone.`
                }
              </p>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancelDeleteTournament}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeleteTournament}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="animate-spin" size={16} />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete Tournament
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete File Confirmation Modal */}
      {showDeleteFileModal && fileToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0">
                  <AlertTriangle className="text-red-500" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete Tournament File
                </h3>
              </div>
              
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete the file <strong>"{fileToDelete.name}"</strong>?
              </p>
              <p className="text-sm text-gray-500 mb-6">
                This will remove all tournament entries and leaderboard data associated with this file. The leaderboard will be automatically recalculated after deletion. This action cannot be undone.
              </p>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancelDeleteFile}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeleteFile}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="animate-spin" size={16} />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete File
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isDatePickerOpen && (
        <DateRangePicker
          isOpen={isDatePickerOpen}
          onClose={() => setIsDatePickerOpen(false)}
          onApply={handleDateRangeApply}
          initialStartDate={
            filters.startDate
              ? moment.tz(filters.startDate, 'YYYY-MM-DD', CST_TIMEZONE).toDate()
              : undefined
          }
          initialEndDate={
            filters.endDate
              ? moment.tz(filters.endDate, 'YYYY-MM-DD', CST_TIMEZONE).toDate()
              : undefined
          }
          initialSelectedRange="thisMonth"
        />
      )}
    </div>
  );
};

export default TournamentLeaderboardPage;
